import {
	type AttachmentInfo,
	type AttachmentListResponse,
	type AttachmentsDeleteRequest,
	OrganisationAttachmentControllerApiFactory,
} from '@api-client'
import { Input } from '@components/Input'
import { Loading } from '@components/Loading'
import { Pagination } from '@components/Pagination'
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableHeaderCell,
	TableRow,
} from '@components/Table'
import { formatDate } from '@helpers/dates'
import { QueryKey } from '@helpers/enums'
import { useAsyncApi } from '@hooks/useAsyncApi'
import { usePagination } from '@hooks/usePagination'
import { DeleteOutlined, Search } from '@mui/icons-material'
import {
	Box,
	Button,
	Checkbox,
	IconButton,
	InputAdornment,
	Modal,
	Typography,
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { useSnackbar } from 'notistack'
import React, { type ChangeEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import * as S from './styles'

interface IProps {
	isModalOpened: boolean
	selectedFiles: readonly AttachmentInfo[]
	onConfirm: (file: readonly AttachmentInfo[]) => void
	onModalClose: (bool: boolean) => void
}

interface IUploadFileButton {
	onUploadFile: (event: ChangeEvent<HTMLInputElement>) => void
}

const UploadFileButton = (props: IUploadFileButton) => {
	const { onUploadFile } = props
	const { t } = useTranslation()

	return (
		<>
			<S.FileUploadInput
				id="file-upload"
				name="file"
				accept="application/pdf"
				type="file"
				onChange={onUploadFile}
				onClick={(event: React.MouseEvent<HTMLInputElement>) => {
					const target = event.currentTarget
					target.value = ''
				}}
			/>
			<label htmlFor="file-upload">
				<Button variant="outlined" color="primary" component="span">
					{t('button.uploadFile')}
				</Button>
			</label>
		</>
	)
}

type Primitive = string | number | boolean | undefined | null
type DeepReadonly<T> = T extends Primitive
	? T
	: T extends (infer U)[]
		? DeepReadonlyArray<U>
		: DeepReadonlyObject<T>
type DeepReadonlyArray<T> = readonly DeepReadonly<T>[]
type DeepReadonlyObject<T> = {
	readonly [P in keyof T]: DeepReadonly<T[P]>
}

const isNewId = (id: string) => id.split('_')[0] === 'new'

const getErrorMessage = (
	status: number,
	t: (key: string) => string
): string => {
	switch (status) {
		case 406:
			return t('toast.error.invalidFile')
		case 413:
			return t('toast.error.fileTooLarge')
		default:
			return t('toast.error.uploadFailed')
	}
}

const SelectFileModal = ({
	isModalOpened,
	selectedFiles,
	onModalClose,
	onConfirm,
}: IProps) => {
	const { t } = useTranslation()

	const [data, setData] = useState<
		DeepReadonly<AttachmentListResponse> | undefined
	>()
	const [searchTerm, setSearchTerm] = useState<string>('')
	const [selected, setSelected] =
		useState<readonly AttachmentInfo[]>(selectedFiles)

	const { totalRows, setTotalRows, currentPage, setCurrentPage } =
		usePagination()
	const { enqueueSnackbar } = useSnackbar()

	const organisationAttachmentControllerApi = useAsyncApi(
		OrganisationAttachmentControllerApiFactory
	)

	const {
		data: filesData,
		isError,
		isLoading,
		refetch,
	} = useQuery({
		queryKey: [QueryKey.ListAttachments, currentPage, totalRows, searchTerm],
		queryFn: () =>
			organisationAttachmentControllerApi(api =>
				api.listAttachments(currentPage, totalRows, searchTerm)
			),
	})

	const onSelect = (item: AttachmentInfo) => {
		if (isNewId(item.id)) {
			return
		}

		const selectedIndex = selected.findIndex(
			(obj: AttachmentInfo) => obj.id === item.id
		)

		let newSelected: AttachmentInfo[] = []
		if (selectedIndex === -1) {
			newSelected = [...selected, item]
		} else if (selectedIndex === 0) {
			newSelected = selected.slice(1)
		} else if (selectedIndex === selected.length - 1) {
			newSelected = selected.slice(0, -1)
		} else if (selectedIndex > 0) {
			newSelected = [
				...selected.slice(0, selectedIndex),
				...selected.slice(selectedIndex + 1),
			]
		}
		setSelected(newSelected)
	}

	const addFile = (
		files: readonly AttachmentInfo[],
		fileObject: File
	): readonly AttachmentInfo[] => {
		const file = {
			id: 'new_' + Math.random(),
			name: fileObject.name,
			createdAt: formatDate(new Date().toString()),
			isUsed: false,
		}
		return [file, ...files]
	}

	const onUploadFile = async (event: ChangeEvent<HTMLInputElement>) => {
		const target = event.target
		const fileObject = (target.files as FileList)[0]

		if (!fileObject || !filesData) {
			return
		}

		const files: readonly AttachmentInfo[] = filesData.data.data
		const { metadata } = filesData.data
		const data = addFile(files, fileObject)

		const newData = { data, metadata: { ...metadata, count: data.length } }

		setData(newData)

		try {
			const response = await organisationAttachmentControllerApi(api =>
				api.uploadAttachment(fileObject)
			)
			if (response.status === 200) {
				refetch()
			}
		} catch (err) {
			if (isAxiosError(err)) {
				const newData = {
					data: files,
					metadata: { ...metadata, count: files.length },
				}

				setData(newData)

				const status = err.response?.status ?? 500
				const errorMessage = getErrorMessage(status, t)

				enqueueSnackbar(errorMessage, {
					variant: 'error',
				})
			}
		}
	}

	const onDelete = async () => {
		const filesSelected: AttachmentsDeleteRequest = {
			ids: selected.map(({ id }) => id),
		}

		try {
			const files = await organisationAttachmentControllerApi(api =>
				api.deleteAttachments(filesSelected)
			)
			if (files.status === 200) {
				refetch()
				setSelected([])
			}
		} catch {
			// Ignore errors on deletion.
		}
	}

	const onSearch = (
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const target: HTMLInputElement = event.target as HTMLInputElement
		const value: string = target.value
		setSearchTerm(value)
	}

	const onConfirmSelection = () => {
		onConfirm(selected)
		onCancel()
	}

	const onCancel = () => {
		setSearchTerm('')
		onModalClose(false)
	}

	useEffect(() => {
		setData(filesData?.data)
	}, [filesData])

	useEffect(() => {
		setSelected(selectedFiles)
	}, [selectedFiles])

	return (
		<Modal open={isModalOpened} onClose={onCancel}>
			<S.BackdropWithModalStyles>
				{isLoading && searchTerm === undefined && <Loading />}

				{(!data || isError) && searchTerm !== '' && <h1>{t('error')}</h1>}

				{data && (
					<>
						<Typography variant="h2">{t('selectFileModal.title')}</Typography>

						<Box
							display="flex"
							alignItems="center"
							justifyContent="space-between"
						>
							<Box mt={3} width={300}>
								<Input
									variant="filled"
									fullWidth
									value={searchTerm}
									name="Search"
									placeholder={t('selectFileModal.searchDocument')}
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<Search />
											</InputAdornment>
										),
									}}
									onChange={onSearch}
								/>
							</Box>
							<Box display="flex" alignItems="center">
								<Box mr={1}>
									<Typography
										variant="subtitle2"
										component="h5"
										color="textSecondary"
									>
										{t('selectFileModal.maxFileSize')}
									</Typography>
								</Box>
								<UploadFileButton onUploadFile={onUploadFile} />
							</Box>
						</Box>
						<Box>
							<Box overflow="auto" height="320px">
								<TableContainer style={{ borderRadius: '10px' }}>
									<Table aria-label="files-uploaded-table" size="small">
										<TableHead>
											<TableRow>
												<TableHeaderCell padding="checkbox" />
												<TableHeaderCell>
													{t('selectFileModal.tableHeaderDocumentName')}
												</TableHeaderCell>
												<TableHeaderCell>
													{t('selectFileModal.tableHeaderUploadDate')}
												</TableHeaderCell>

												<TableHeaderCell align="center" padding="checkbox">
													<IconButton
														onClick={onDelete}
														disabled={selected.length === 0}
													>
														<DeleteOutlined />
													</IconButton>
												</TableHeaderCell>
											</TableRow>
										</TableHead>

										<TableBody>
											{data &&
												data.data.length > 0 &&
												data.data.map(item => (
													<TableRow
														tabIndex={-1}
														role="checkbox"
														onClick={() =>
															onSelect({
																id: item.id,
																name: item.name,
																createdAt: item.createdAt,
																isUsed: item.isUsed,
															})
														}
														key={item.id}
													>
														<TableCell>
															{isNewId(item.id) && (
																<Box
																	display="flex"
																	p={1.2}
																	justifyContent="center"
																>
																	<Loading size="small" noWrapper />
																</Box>
															)}
															{!isNewId(item.id) && (
																<Checkbox
																	checked={selected.some(
																		obj => obj.id === item.id
																	)}
																/>
															)}
														</TableCell>
														<TableCell>
															<S.NameWrapper>{item.name}</S.NameWrapper>
														</TableCell>
														<TableCell colSpan={2}>
															{formatDate(item.createdAt)}
														</TableCell>
													</TableRow>
												))}

											{data && data.data.length === 0 && (
												<TableRow>
													<TableCell colSpan={4} scope="row" component="td">
														{searchTerm === '' ? (
															<>
																<Box my={2}>
																	<Typography variant="h6" align="center">
																		{t('selectFileModal.noFileUploaded')}
																	</Typography>
																</Box>
																<Typography variant="body1" align="center">
																	{t('selectFileModal.clickToUpload')}
																</Typography>
															</>
														) : (
															<>
																<Box my={2}>
																	<Typography variant="h6" align="center">
																		{t('selectFileModal.noMatch', {
																			word: searchTerm,
																		})}
																	</Typography>
																</Box>
																<Typography variant="body1" align="center">
																	{t('selectFileModal.clickToUpload')}
																</Typography>
															</>
														)}
														<Box my={2} display="flex" justifyContent="center">
															<UploadFileButton onUploadFile={onUploadFile} />
														</Box>
													</TableCell>
												</TableRow>
											)}
										</TableBody>
									</Table>
								</TableContainer>
							</Box>

							{data && (
								<Pagination
									rows={data.metadata.count}
									itemsPerPage={totalRows}
									setItemsPerPage={setTotalRows}
									currentPage={currentPage}
									setCurrentPage={setCurrentPage}
								/>
							)}
						</Box>

						<Box display="flex" justifyContent="flex-end" pt={1.5}>
							<Box mr={2}>
								<Button variant="outlined" color="primary" onClick={onCancel}>
									{t('button.cancel')}
								</Button>
							</Box>
							<Button
								variant="contained"
								color="primary"
								disabled={selected.length === 0}
								onClick={onConfirmSelection}
							>
								{t('button.confirm')}
							</Button>
						</Box>
					</>
				)}
			</S.BackdropWithModalStyles>
		</Modal>
	)
}

export { SelectFileModal }
